import { type TelegramUser } from "@features/auth/hooks/useTelegramAuth";
import axios from "axios";

import { toast } from "@/shared/helper_lib/hooks/use-toast";

const API_URL = import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8050";

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json"
  }
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 429) {
      // Получаем информацию о лимите из заголовков ответа, если доступно
      const retryAfter = error.response?.headers?.["retry-after"];
      const endpoint = error.config?.url || "";

      let title = "Превышен лимит запросов";
      let description = "Попробуйте позже.";

      // Специфичные сообщения для разных эндпоинтов
      if (endpoint.includes("/upload/init")) {
        title = "Слишком много попыток загрузки";
        description = "Превышен лимит: 30 загрузок в час. Пожалуйста, подождите и попробуйте снова.";
      } else if (endpoint.includes("/transcription")) {
        title = "Слишком много запросов на транскрипцию";
        description = "Превышен лимит: 30 транскрипций в час. Пожалуйста, подождите и попробуйте снова.";
      } else if (retryAfter) {
        const seconds = parseInt(retryAfter);
        const minutes = Math.ceil(seconds / 60);
        description = `Попробуйте снова через ${minutes} мин.`;
      }

      toast({
        variant: "destructive",
        title,
        description
      });
    }
    return Promise.reject(error);
  }
);

export interface AuthResponse {
  user: {
    id: string;
    email: string | null;
    name: string;
    picture?: string;
  };
}

export interface UserProfile {
  id: string;
  email: string | null;
  name: string;
  picture?: string;
  role?: "user" | "superadmin";
}

export interface ConsentData {
  agreedToOffer: boolean;
  agreedToPrivacy: boolean;
}

export const authApi = {
  googleAuth: async (idToken: string, consents?: ConsentData): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>("/auth/google", {
      idToken,
      ...consents
    });
    return response.data;
  },

  googleCodeAuth: async (code: string, consents?: ConsentData): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>("/auth/google/code", {
      code,
      ...consents
    });
    return response.data;
  },

  yandexAuth: async (accessToken: string, consents?: ConsentData): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>("/auth/yandex", {
      accessToken,
      ...consents
    });
    return response.data;
  },

  telegramAuth: async (user: TelegramUser, consents?: ConsentData): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>("/auth/telegram", {
      ...user,
      ...consents
    });
    return response.data;
  },

  getProfile: async (): Promise<UserProfile> => {
    const response = await api.get<UserProfile>("/auth/profile");
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post("/auth/logout");
  },

  updateEmail: async (email: string): Promise<UserProfile> => {
    const response = await api.patch<UserProfile>("/auth/email", { email });
    return response.data;
  }
};

export default api;
